'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';

import {
	type ComponentPropsWithoutRef,
	type ComponentRef,
	forwardRef,
} from 'react';
import { cn } from '../../utils/cn';
import type {
	AccordionContentProps,
	AccordionItemProps,
	AccordionTriggerProps,
} from './types';
import {
	accordionContentContainerStyles,
	accordionItemVariants,
	accordionTriggerChevronStyles,
	accordionTriggerContentStyles,
	accordionTriggerVariants,
	accordionVariants,
} from './variants';

const AccordionComponents = AccordionPrimitive.Root;

const AccordionItem = forwardRef<
	ComponentRef<typeof AccordionPrimitive.Item>,
	AccordionItemProps
>(({ className, variant = 'normal', ...props }, ref) => (
	<AccordionPrimitive.Item
		ref={ref}
		className={cn(accordionItemVariants({ variant }), className)}
		{...props}
	/>
));

AccordionItem.displayName = 'AccordionItem';

const AccordionHeader = forwardRef<
	ComponentRef<typeof AccordionPrimitive.Header>,
	ComponentPropsWithoutRef<typeof AccordionPrimitive.Header>
>(({ className, ...props }, ref) => (
	<AccordionPrimitive.Header
		ref={ref}
		className={cn('flex', className)}
		{...props}
	/>
));
AccordionHeader.displayName = AccordionPrimitive.Header.displayName;

const AccordionTrigger = forwardRef<
	ComponentRef<typeof AccordionPrimitive.Trigger>,
	AccordionTriggerProps
>(({ className, variant = 'normal', children, ...props }, ref) => (
	<AccordionHeader>
		<AccordionPrimitive.Trigger
			ref={ref}
			className={cn(accordionTriggerVariants({ variant }), className)}
			{...props}
		>
			<span
				className={clsx(accordionTriggerContentStyles)}
				accordion-trigger-content=""
			>
				{children}
			</span>
			<ChevronRight className={clsx(accordionTriggerChevronStyles)} />
		</AccordionPrimitive.Trigger>
	</AccordionHeader>
));

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = forwardRef<
	ComponentRef<typeof AccordionPrimitive.Content>,
	AccordionContentProps
>(({ className, variant = 'normal', children, ...props }, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className={clsx(accordionContentContainerStyles)}
		{...props}
	>
		<div className={cn(accordionVariants({ variant }), className)}>
			{children}
		</div>
	</AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
	AccordionComponents,
	AccordionContent,
	AccordionHeader,
	AccordionItem,
	AccordionTrigger,
};
